import { useMemo, useState } from "react";

import {
  Box,
  Center,
  Loader,
  ScrollArea,
  Space,
  Stack,
  Text,
} from "@mantine/core";
import useMantineMediaQueries from "~/../../base-components/lib/hooks/useMantineMediaQueries";

import {
  Condition,
  ContractNft,
  NetworkType,
  NftStandardType,
} from "apollo/graphql.generated";

import {
  ContractDetailsQuery,
  ContractNftsQuery,
  useContractNftQuery,
  useContractNftsQuery,
} from "../../queries/gates.graphql.generated";
import SectionFilterResults from "../condition-modals/sections/SectionFilterResults";

import {
  SimpleInfiniteGrid,
  SimpleInfiniteListItem,
} from "./SimpleInfiniteGrid";

type ContractNfts = ContractNftsQuery["contractNftsPaginated"]["nfts"];
export type TokenDetails = {
  id: string;
  name: string;
  contract?: string;
  address?: string;
  imageUrl?: string;
  videoUrl?: string;
  standard?: NftStandardType;
};

function LoadingElement() {
  return (
    <Center p={"lg"} h={500}>
      <Loader size={32} />
    </Center>
  );
}

function NoResultsElement({ text }: { text?: string }) {
  return (
    <Center p={"lg"} h={500}>
      <Stack align="center">
        <Text size={"lg"}>{text || "No results."}</Text>
      </Stack>
    </Center>
  );
}

export type SelectedNftContractCondition = {
  network?: NetworkType;
  contract?: string;
  openSeaUrl?: string;
  token?: string;
  condition?: Condition;
};

export function ContractCollectionTokenGrid(props: {
  contractCollection: ContractDetailsQuery["contractDetails"];
  network: NetworkType;
  selectedToken?: SimpleInfiniteListItem<ContractNft>;
  onSelectToken: (tokenDetails: SimpleInfiniteListItem<ContractNft>) => void;
  initialTokenId?: string;
}) {
  const {
    contractCollection,
    network,
    onSelectToken,
    selectedToken,
    initialTokenId,
  } = props;
  const { isMedium } = useMantineMediaQueries();
  const [cursor, setCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [searchToken, setSearchToken] = useState<string>(initialTokenId ?? "");
  const [paginatedContractNfts, setPaginatedContractNfts] =
    useState<ContractNfts>();

  const { loading: loadingContractNfts } = useContractNftsQuery({
    variables: {
      network: network!,
      address: contractCollection.contract || "",
      cursor,
    },
    skip: !(
      contractCollection &&
      network &&
      contractCollection.standard &&
      contractCollection.contract
    ),
    onCompleted(data) {
      const data2 = data.contractNftsPaginated;
      setNextCursor(data2.pageInfo.endCursor);
      setPaginatedContractNfts((paginatedContractNfts) =>
        (paginatedContractNfts ?? []).concat(data2.nfts)
      );
    },
  });

  const { data: contractNft, loading: loadingContractNft } =
    useContractNftQuery({
      variables: {
        network: network!,
        address: contractCollection.contract,
        contractNftId: searchToken!,
      },
      skip: !(
        contractCollection &&
        network &&
        contractCollection.standard &&
        contractCollection.contract &&
        searchToken
      ),
    });

  const mappedContractNfts = useMemo<
    SimpleInfiniteListItem<ContractNft>[]
  >(() => {
    if (searchToken !== "") {
      const nft = contractNft?.contractNft;
      if (nft == null) return [];
      return [
        {
          key: `${nft.contract}-${nft.id}`,
          id: nft.id,
          title: nft.name,
          imageUrl: nft.imageUrl ?? "",
          data: nft,
        },
      ];
    }

    if (paginatedContractNfts == null) return [];

    return paginatedContractNfts.map((nft) => {
      return {
        key: `${nft.contract}-${nft.id}`,
        id: nft.id,
        title: nft.name,
        imageUrl: nft.imageUrl ?? "",
        data: nft,
      };
    });
  }, [paginatedContractNfts, contractNft?.contractNft, searchToken]);

  return (
    <Box>
      <SectionFilterResults
        query={searchToken || ""}
        onChangeQuery={setSearchToken}
        isLoading={loadingContractNft}
      />

      <Space h={"xl"} />

      <ScrollArea.Autosize mih={100} maxHeight={"auto"}>
        <SimpleInfiniteGrid<ContractNft>
          items={mappedContractNfts}
          hasMore={searchToken === "" && nextCursor != null}
          loading={loadingContractNft || loadingContractNfts}
          loadMore={() => setCursor(nextCursor)}
          selectedItem={selectedToken}
          onSelectItem={onSelectToken}
          loadingElement={<LoadingElement />}
          emptyResultsElement={<NoResultsElement />}
          gridCols={isMedium ? 3 : 1}
        />
      </ScrollArea.Autosize>
    </Box>
  );
}
